import * as stylex from '@stylexjs/stylex';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, redirect, useLocation } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { ResizableTableContainer, Table, TableLayout, Virtualizer } from 'react-aria-components';
import {
  array as vArray,
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
  unknown as vUnknown,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { TableRowSecondaryActions } from '@/components/table-row-secondary-actions.component';
import {
  Breadcrumb,
  Breadcrumbs,
  Cell,
  Column,
  EmptyState,
  Heading,
  Row,
  Skeleton,
  type SkeletonRow,
  TableBody,
  TableHeader,
  TableOverlay,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { auditLogQueries, type AuditLogTableRow } from '@/resources/audit-log/audit-log.api';
import { auditLogPolicies } from '@/resources/audit-log/audit-log.policies';
import { campaignActivityTableOptions } from '@/resources/audit-log/audit-log.table';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Resource } from '@/types/resource.type';

const DEFAULT_SORTING = { id: 'createdAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 50 };

export const Route = createFileRoute('/_main/_list/campaigns/$campaignId/activity')({
  component: RouteComponent,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    columnFilters: vFallback(vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []), []),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  beforeLoad: ({ search, params, context }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    if (context.user && !auditLogPolicies.bindContext({ user: context.user.original }).view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: `campaign-${Resource.activityLog}`,
      queryOptions: {
        list: auditLogQueries.list({
          id: params.campaignId,
          resource: Resource.campaignRequest,
          limit: search.pagination.pageSize,
          page: search.pagination.pageIndex + 1,
        }),
        campaignRequestDetail: campaignRequestQueries.detail(params.campaignId),
      },
    };
  },
  loader: async ({ context }) => {
    if (!context.user) {
      return undefined;
    }

    const campaignRequestDetailData = await context.queryClient.ensureQueryData(context.queryOptions.campaignRequestDetail);

    if (!campaignRequestDetailData) {
      throw redirect({ to: '/campaigns' });
    }

    await context.queryClient.ensureQueryData(context.queryOptions.list);

    return { detail: campaignRequestDetailData };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `TrendMD | ${loaderData?.detail.campaignName ?? 'Campaign'} - Activity`,
      },
    ],
  }),
  staticData: {
    list: {
      heading: 'Activity',
      slots: {
        topBarComponent: TopBar,
        toolbarActionsComponent: ToolbarActionsComponent,
        toolbarLeftComponent: ToolbarLeftComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const location = useLocation();

  const [ columnsConfig ] = useLocalStorage(`table-columns-${routeContext.resource}`, campaignActivityTableOptions.initialConfig);

  const query = useQuery(routeContext.queryOptions.list);

  const dataRowsForConfig: AuditLogTableRow[] = query.isLoading
    ? []
    : query.data?.data ?? [];

  const config = campaignActivityTableOptions(dataRowsForConfig, columnsConfig);
  const columns = config.getColumns();

  const [ hasMeasuredData, setHasMeasuredData ] = useState(false);
  if (!hasMeasuredData && !!dataRowsForConfig.length) {
    setHasMeasuredData(true);
  }

  const rows: Array<AuditLogTableRow | SkeletonRow> = query.isLoading ? config.getSkeletonRows() : dataRowsForConfig;

  return (
    <TableOverlay
      isFullScreen
      rows={rows}
      isRowHighlighted={(row) => {
        if (config.isSkeletonRow(row)) {
          return false;
        }
        return location.hash === `${routeContext.resource}-${row.original.id}`;
      }}
      renderEmptyState={() => (
        !query.isLoading && <EmptyState><Heading>No data available</Heading></EmptyState>
      )}
      renderRowActions={(row) => {
        if (config.isSkeletonRow(row)) {
          return null;
        }
        return (
          <TableRowSecondaryActions
            hash={`${routeContext.resource}-${row.original.id}`}
            id={row.original.id.toString()}
          />
        );
      }}>
      <Virtualizer layout={TableLayout} layoutOptions={config.layoutOptions}>
        <ResizableTableContainer key={hasMeasuredData ? 'data' : 'loading'} {...stylex.props(resizableTableContainerStyles.base)}>
          <Table
            aria-label="Activity"
            selectionMode="none"
            style={{ borderSpacing: 0 }}>
            <TableHeader columns={columns}>
              {(column) => <Column {...column} isFullScreen />}
            </TableHeader>
            <TableBody<typeof rows[number]> dependencies={[ columns, query.isLoading ]}>
              {(item) => {
                const isSkeleton = config.isSkeletonRow(item);

                if (isSkeleton) {
                  return (
                    <Row columns={columns} id={item.id}>
                      {() => (
                        <Cell isFullScreen>
                          <Skeleton />
                        </Cell>
                      )}
                    </Row>
                  );
                }

                const row = item as AuditLogTableRow;
                return (
                  <Row
                    columns={columns}
                    id={row.original.id}
                    variant="primary">
                    {(column) => {
                      const cellProps = config.getCell(column.id, row);
                      return cellProps ? <Cell {...cellProps} isFullScreen /> : <Cell isFullScreen />;
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </ResizableTableContainer>
      </Virtualizer>
    </TableOverlay>
  );
}

function TopBar(): ReactElement {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestQueries.detail(routeParams.campaignId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/campaigns/$campaignId">
          {detailQuery.data?.original.campaignName}
        </RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/campaigns/$campaignId/activity">Activity</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function ToolbarActionsComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);
  return !isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

function ToolbarLeftComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);
  return isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

const resizableTableContainerStyles = stylex.create({
  base: {
    height: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    willChange: 'transform',
  },
});

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
